// @ts-nocheck
/* global css */
import React, {
  ChangeEvent, useMemo, useState,
} from 'react';
import cx from 'classnames';
import THEME from '../../shared/theme';
import { cssInJS } from '../../utils/styles';

const OTHER_VALUE = '__OTHER__';

interface IOption {
  value: string,
  label: string,
}

export interface IValue {
  hasOtherValue?: boolean;
  otherValue?: string;
  selectedValues?: string[];
}

interface Props {
  value: IValue;
  disabled?: boolean;
  onChange: (newVal: IValue) => void;
  choices: IOption[];
  allowOther?: boolean;
  multiple?: boolean;
  emptyValue?: string;
  emptyLabel?: string;
}
/**
 * @deprecated this file has been replaced by material
 */
export const ChoiceField: React.FC<Props> = ({
  value, disabled, onChange, choices, allowOther, multiple, emptyLabel = '',
}: Props) => {
  const { hasOtherValue, otherValue, selectedValues } = value;
  const [isOtherEnabled, setIsOtherEnabled] = useState(hasOtherValue);
  const [otherValueChoice, setOtherValueChoice] = useState(otherValue);

  const containerClasses = css(styles.container);
  const selectClasses = css(styles.select, multiple && styles.multiple);
  const optionClasses = css(styles.option, multiple && styles.multipleOption);
  const fixedChoices = allowOther ? choices + 2 : choices + 1;
  const size = multiple ? fixedChoices : null;
  const options = useMemo(() => {
    const otherOpt = {
      label: 'Other',
      value: OTHER_VALUE,
    };
    const optionElements = [{ label: emptyLabel, value: '' }, ...choices];
    if (allowOther) {
      optionElements.push(otherOpt);
    }

    return optionElements;
  }, [choices]);

  const selected: string | string[] = useMemo(() => {
    const otherSelected = isOtherEnabled ? [OTHER_VALUE] : [];
    const otherSelectedValues = [...selectedValues, ...otherSelected];
    const hasSelectedValues = otherSelectedValues && otherSelectedValues.length;
    const selectedMultipleValues = hasSelectedValues ? otherSelectedValues : '';
    const selectedSingleValues = otherSelectedValues[0];
    const isSingleSelection = hasSelectedValues && !multiple;
    return isSingleSelection ? selectedSingleValues : selectedMultipleValues;
  }, [selectedValues]);

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const eventOptions = Array.from(event.target.options);
    const values = eventOptions
      .filter((opt) => opt.selected && opt.value.length)
      .map((opt) => opt.value);
    const otherEnabled = values
      .some((opt) => opt === OTHER_VALUE);
    const valuesSelected = values
      .filter((opt) => opt !== OTHER_VALUE);
    const otherValues = otherEnabled
      ? [otherValueChoice]
        .filter((opt) => !!opt) : [];
    const valueUpdated = value;
    valueUpdated.selectedValues = valuesSelected;
    valueUpdated.otherValues = otherValues;
    onChange(valueUpdated);
    setIsOtherEnabled(otherEnabled);
  };

  const handleOtherValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const otherValueChanged = event.target.value;
    const valueUpdated = value;
    valueUpdated.otherValues = [otherValueChanged];
    onChange(valueUpdated);
    setOtherValueChoice(otherValueChanged);
  };

  return (
    <div className={cx(containerClasses)}>
      <select
        multiple={multiple}
        disabled={disabled}
        value={selected || (multiple ? [] : '')}
        onChange={handleSelectChange}
        size={size}
        className={cx(selectClasses)}
      >
        {
          options.map((choice: IOption) => (
            <option
              key={choice.value + choice.label}
              value={choice.value}
              className={cx(optionClasses)}
            >
              {choice.label}
            </option>
          ))
        }
      </select>
      {
        isOtherEnabled
        && (
          <input
            type="text"
            disabled={disabled}
            className={css(styles.otherValue)}
            value={otherValueChoice || ''}
            onChange={handleOtherValueChange}
            placeholder="Enter Other Value"
          />
        )
      }
    </div>
  );
};

const styles = cssInJS({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexFlow: 'wrap',
    alignContent: 'end',
    width: '100%',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  otherValue: {
    fontSize: THEME.editFontSize,
    border: '1px solid #d9d9d9',
    width: '100%',
    borderRadius: 5,
    padding: '0.5rem',
  },

  select: {
    fontFamily: THEME.fontFamily,
    fontSize: THEME.editFontSize,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    display: 'inline-block',
    verticalAlign: 'middle',
    borderRadius: 5,
    width: '100%',
    padding: '0.5rem',
    marginBottom: '0.2rem',
    // '-webkit-appearance': 'none',

    '@phone': {
      width: '100%',
    },
  },

  multiple: {
    height: 'auto',
    borderRadius: 5,
    maxHeight: 300,
    overflow: 'auto',
    // boxSizing: 'content-box'
  },

  option: {},

  multipleOption: {
    boxSizing: 'content-box',
  },
});

export default ChoiceField;
